import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import anniversary20Years from "../../../assets/images/home/anniversaryicon.svg";
import intertekLogo from "../../../assets/images/home/intertek.svg";
import carouselNeptune from "../../../assets/images/home/carousel-neptune.png";
import carouselControllers from "../../../assets/images/home/carousel-controllers.png";
import carouselMobileApps from "../../../assets/images/home/carousel-mobile-apps.png";
import carouselReaders from "../../../assets/images/home/carousel-readers.png";
import carouselDirectory from "../../../assets/images/home/carousel-directory.png";
import carouselMiniPc from "../../../assets/images/home/carousel-mini-pc.png";
import carouselCredentials from "../../../assets/images/home/carousel-credentials.png";

const PAGE_X = "px-4 sm:px-6 lg:px-[120px]";
const NAV_OFFSET = "pt-[calc(64px+50px)]";
const FADE =
  "transition-opacity duration-500 ease-in-out motion-reduce:transition-none";

const GLOW_STYLE = {
  background:
    "radial-gradient(71.76% 71.76% at 50% 50%, rgba(191, 209, 255, 0.19) 21.15%, rgba(255, 255, 255, 0.19) 68.74%)",
  filter: "blur(20.244104385375977px)",
};

const SLIDES = [
  {
    title: "Neptune 2.0",
    subtitle: "Rock solid time tested",
    image: carouselNeptune,
    imgWidth: 674,
    imgHeight: 397,
    glow: null,
    path: "/user-interface",
    showIntertek: true,
  },
  {
    title: "Controllers",
    subtitle: "Meet our scalable 1-8 doors controllers",
    image: carouselControllers,
    imgWidth: 1135,
    imgHeight: 467,
    glow: { width: 1135, height: 414 },
    path: "/product?category=controller",
    showIntertek: true,
  },
  {
    title: "Mobile Apps",
    subtitle: "Control everything on mobile Apps",
    image: carouselMobileApps,
    imgWidth: 490,
    imgHeight: 250,
    glow: { width: 637, height: 376 },
    path: "/mobile-applications",
  },
  {
    title: "Readers",
    subtitle: "Meet total access for doors to High-Security readers",
    image: carouselReaders,
    imgWidth: 491,
    imgHeight: 299,
    glow: { width: 1134, height: 418 },
    path: "/product?category=reader",
  },
  {
    title: "Tenant 8",
    subtitle: "Door Controller Intercom",
    image: carouselDirectory,
    imgWidth: 491,
    imgHeight: 391,
    glow: { width: 637, height: 376 },
    path: "/product?category=directory",
  },
  {
    title: "Mini PC",
    subtitle: "Sicunet Solid State Server",
    image: carouselMiniPc,
    imgWidth: 590,
    imgHeight: 422,
    glow: { width: 637, height: 376 },
    path: "/product?category=mini%20pc",
  },
  {
    title: "Credentials",
    subtitle: "Experience card or mobile",
    image: carouselCredentials,
    imgWidth: 496,
    imgHeight: 350,
    glow: { width: 637, height: 376 },
    path: "/product?category=credential",
  },
];

const CarouselArrow = ({ direction, onClick, hidden, className = "" }) => (
  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    aria-label={direction === "prev" ? "Previous slide" : "Next slide"}
    className={`z-30 flex items-center justify-center w-9 h-9 sm:w-[42px] sm:h-[42px] rounded-full bg-[#E8F0FF]/90 shadow-sm transition-opacity shrink-0 ${hidden ? "opacity-0 pointer-events-none" : "hover:bg-[#d6e4ff]"
      } ${className}`}
  >
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      className={direction === "prev" ? "rotate-180" : ""}
      aria-hidden="true"
    >
      <path
        d="M6 3L11 8L6 13"
        stroke="#2F6BFF"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </button>
);

const BtnLearnMore = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex items-center justify-center w-[121px] h-[40px] bg-[#2F6BFF] text-[#f5f7fa] text-sm font-medium font-['Poppins'] rounded-[6px] hover:bg-[#2560e6] transition-colors shrink-0"
  >
    Learn more
  </button>
);

const PaginationDots = ({ count, active, onSelect }) => (
  <div className="flex items-center justify-center gap-2 h-2 shrink-0">
    {Array.from({ length: count }, (_, i) => (
      <button
        key={i}
        type="button"
        onClick={() => onSelect(i)}
        aria-label={`Go to slide ${i + 1}`}
        aria-current={i === active ? "true" : undefined}
        className={`rounded-full transition-colors duration-300 size-2 ${i === active ? "bg-[#2F6BFF]" : "bg-[#D9D9D9]"
          }`}
      />
    ))}
  </div>
);

const ImageGlow = ({ glow }) => {
  if (!glow) return null;
  return (
    <div
      className="absolute left-1/2 bottom-0 -translate-x-1/2 pointer-events-none w-[min(100%,640px)] max-w-full"
      style={{
        aspectRatio: `${glow.width} / ${glow.height}`,
        maxHeight: glow.height,
        borderRadius: "637px",
        ...GLOW_STYLE,
      }}
      aria-hidden="true"
    />
  );
};

const CarouselTopLogos = ({ showIntertek }) => (
  <div className="flex items-center justify-end gap-3 sm:gap-9 max-w-full">
    {showIntertek && (
      <img
        src={intertekLogo}
        alt="Intertek Certified"
        className="block shrink-0 w-auto h-[52px] sm:h-[68px] lg:h-[86px] object-contain"
        draggable={false}
      />
    )}
    <img
      src={anniversary20Years}
      alt="20 Years Anniversary"
      className="block shrink-0 w-auto h-[52px] sm:h-[68px] lg:h-[86px] object-contain"
      draggable={false}
    />
  </div>
);

export const HomeCarousel = () => {
  const navigate = useNavigate();
  const [active, setActive] = useState(0);

  const goPrev = useCallback(
    () => setActive((prev) => Math.max(0, prev - 1)),
    []
  );
  const goNext = useCallback(
    () => setActive((prev) => (prev + 1) % SLIDES.length),
    []
  );

  useEffect(() => {
    const interval = setInterval(goNext, 5000);
    return () => clearInterval(interval);
  }, [goNext, active]);

  // ✅ Fix - parse pathname and search separately
  const goToSlide = (path) => {
    const [pathname, search] = path.split("?");
    navigate({ pathname, search: search ? `?${search}` : "" });
  };

  return (
    <section
      className={`w-full bg-white ${PAGE_X} ${NAV_OFFSET} pb-8 lg:pb-10`}
    >
      <div className="max-w-[1440px] mx-auto w-full">
        <div className="relative isolate flex flex-col items-center w-full overflow-visible">
          {/* Top Logos - mobile only, appears first */}
          <div className="relative z-[2] pointer-events-none w-full mb-6 lg:mb-0 lg:hidden">
            <div className="relative min-h-[70px] sm:min-h-[95px] w-full">
              {SLIDES.map((s, i) => (
                <div
                  key={`logos-mobile-${s.title}`}
                  className={`absolute right-0 top-0 max-w-full ${FADE} ${i === active
                    ? "opacity-100 z-10"
                    : "opacity-0 z-0 pointer-events-none"
                    }`}
                  aria-hidden={i !== active}
                >
                  <CarouselTopLogos showIntertek={s.showIntertek} />
                </div>
              ))}
            </div>
          </div>

          {/* Top Row (lg+): Title Section (center) + Logos (right) */}
          <div className="relative z-[2] w-full pt-0 lg:pt-14 mb-6 lg:mb-8 hidden lg:block">
            <div className="relative min-h-[70px] sm:min-h-[95px] w-full flex items-end">
              {/* Title Section - absolutely centered in the row */}
              <div className="absolute inset-0 flex items-end justify-center translate-y-2 sm:translate-y-3">
                {SLIDES.map((s, i) => (
                  <div
                    key={`text-desktop-${s.title}`}
                    className={`absolute flex flex-col items-center gap-3 sm:gap-4 text-center ${FADE} ${i === active
                      ? "opacity-100 pointer-events-auto z-10"
                      : "opacity-0 pointer-events-none z-0"
                      }`}
                    aria-hidden={i !== active}
                  >
                    <h2 className="text-2xl sm:text-[32px] font-medium font-['Inter'] text-black leading-tight">
                      {s.title}
                    </h2>
                    <p className="text-base sm:text-[20px] font-medium font-['Inter'] text-[#5b5b5b] leading-snug">
                      {s.subtitle}
                    </p>
                    <BtnLearnMore onClick={() => goToSlide(s.path)} />
                  </div>
                ))}
              </div>

              {/* Top Logos - right side, shifted up 12px */}
              <div className="relative shrink-0 ml-auto -translate-y-3">
                {SLIDES.map((s, i) => (
                  <div
                    key={`logos-desktop-${s.title}`}
                    className={`${FADE} ${i === active
                      ? "opacity-100 z-10"
                      : "opacity-0 z-0 pointer-events-none absolute inset-0"
                      }`}
                    aria-hidden={i !== active}
                  >
                    <CarouselTopLogos showIntertek={s.showIntertek} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Title Section - Above Image (mobile only, appears second/below logos) */}
          <div className="relative w-full flex flex-col items-center text-center mb-6 lg:mb-8 lg:hidden min-h-[160px] sm:min-h-[180px]">
            {SLIDES.map((s, i) => (
              <div
                key={`text-mobile-${s.title}`}
                className={`absolute flex flex-col items-center gap-5 sm:gap-8 ${FADE} ${i === active
                  ? "opacity-100 pointer-events-auto z-10"
                  : "opacity-0 pointer-events-none z-0"
                  }`}
                aria-hidden={i !== active}
              >
                <h2 className="text-2xl sm:text-[32px] font-medium font-['Inter'] text-black leading-tight">
                  {s.title}
                </h2>
                <p className="text-lg sm:text-[24px] font-medium font-['Inter'] text-[#5b5b5b] leading-snug px-2">
                  {s.subtitle}
                </p>
                <BtnLearnMore onClick={() => goToSlide(s.path)} />
              </div>
            ))}
          </div>

          {/* Image Carousel with Arrows */}
          <div className="relative w-full flex items-center justify-center lg:gap-6">
            <CarouselArrow
              direction="prev"
              onClick={goPrev}
              hidden={active === 0}
              className="absolute left-0 top-[60%] -translate-y-1/2 lg:static lg:translate-y-0 lg:mt-14 z-30"
            />

            <div className="relative w-full min-w-0 overflow-hidden h-[clamp(260px,64vw,480px)] max-w-[1138px] mx-auto isolate">
              {SLIDES.map((s, i) => (
                <div
                  key={`image-${s.title}`}
                  className={`absolute inset-0 flex items-end justify-center px-1 sm:px-2 ${FADE} ${i === active
                    ? "opacity-100 z-[1]"
                    : "opacity-0 z-0 pointer-events-none"
                    }`}
                  aria-hidden={i !== active}
                >
                  <ImageGlow glow={s.glow} />

                  <button
                    type="button"
                    onClick={() => goToSlide(s.path)}
                    tabIndex={i === active ? 0 : -1}
                    className="relative z-[1] flex h-full w-full max-w-full items-end justify-center cursor-pointer bg-transparent border-0 p-0"
                    aria-label={`View ${s.title}`}
                  >
                    <img
                      src={s.image}
                      alt={s.title}
                      width={s.imgWidth}
                      height={s.imgHeight}
                      draggable={false}
                      className="block h-full w-full object-contain object-bottom"
                    />
                  </button>
                </div>
              ))}
            </div>

            <CarouselArrow
              direction="next"
              onClick={goNext}
              hidden={active === SLIDES.length - 1}
              className="absolute right-0 top-[60%] -translate-y-1/2 lg:static lg:translate-y-0 lg:mt-14 z-30"
            />
          </div>

          {/* Pagination Dots */}
          <div className="mt-8 sm:mt-12">
            <PaginationDots
              count={SLIDES.length}
              active={active}
              onSelect={setActive}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeCarousel;